<script setup lang="ts">
/**
 * @author Beka Chkhaidze
 * @emits select
 * @note component inherits Input.vue's props and will be binded automatically
 * @summary you should pass items props array
 *
 * {
 *  title: string;
 *  value: string;
 *  isActive: boolean
 * }
 * if 'isActive' is true, item will be considered as selected
 */
import { useMainStore } from "@/store/main/index";
import type { SelectInterface } from "@/types/FormInterface";

import DownChevron from "@/components/regularComponents/UIComponents/SVG/chevrons/Down.vue";
import Dropdown from "@/components/regularComponents/UIComponents/Dropdown.vue";

const { t } = useI18n();

const mainStore = useMainStore();
const windowWidth = computed(() => mainStore.windowWidth);
const label = ref(`${t("filter by: year")} `);
const props = defineProps<{
  items: SelectInterface[];
  rightSlotText?: string;
}>();

const handleResize = () => {
  if (windowWidth.value <= mainStore.breakpoints.mobile_sm) {
    label.value = `${t("year:")} `;
  } else {
    label.value = selectedItem.value ? `${t("filter by:")} ` : `${t("filter by: year")} `;
  }
};

watch(windowWidth, (newWidth, oldWidth) => {
  handleResize();
});
onMounted(() => {
  handleResize();
});
const showTitle = ref(true);

const emit = defineEmits<{
  (e: "select", item: SelectInterface | undefined): void;
}>();

const isCollapsed = ref(false);
const selectedItem = ref<SelectInterface>();

const onClickOutside = () => {
  isCollapsed.value = false;
};

const onSelect = (item: SelectInterface) => {
  selectedItem.value = item;
  isCollapsed.value = false;
  emit("select", selectedItem.value);
  handleResize();
};

watch(
  props,
  (n) => {
    const match = props.items.find((el) => el.isActive);
    if (match) {
      selectedItem.value = match;
    }
  },
  {
    immediate: true,
  }
);
</script>

<template>
  <section class="custom-filter" v-click-outside="onClickOutside">
    <div
      class="custom-filter__dropdown"
      :class="{
        'custom-filter__dropdown--active': isCollapsed,
        'is-selected': selectedItem,
        'isnot-selected': !selectedItem,
      }"
      @click="isCollapsed = !isCollapsed"
    >
      <span v-if="showTitle" class="custom-filter__dropdown-title">{{ label }}</span>
      <span
        v-if="selectedItem"
        class="custom-filter__dropdown-choice"
        :class="{
          'custom-filter__dropdown-choice--active': !isCollapsed && selectedItem,
        }"
      >
        {{ selectedItem?.title }}
      </span>
      <span class="custom-filter__dropdown-chevron">
        <DownChevron
          class="custom-filter__chevron"
          :class="{ 'custom-filter__chevron--active': isCollapsed }"
        />
      </span>
    </div>

    <Dropdown :active="isCollapsed" hideIcon>
      <template #body>
        <ul v-if="items?.length">
          <li
            v-for="(item, index) in items"
            :key="index"
            @click="onSelect(item)"
            :class="[
              'custom-filter__item',
              {
                'custom-filter__item--active': selectedItem?.value == item.value,
              },
            ]"
          >
            {{ item?.title }}
          </li>
        </ul>
        <p v-else class="custom-filter__item op-3 ev-none text-center">
          {{ $t("list is empty") }}
        </p>
      </template>
    </Dropdown>
  </section>
</template>

<style lang="scss">
.custom-filter {
  &__dropdown {
    display: flex;
    align-items: center;
    padding: 20px 18px 20px 16px;

    cursor: pointer;
    &.is-selected {
      text-transform: capitalize;
      background-color: rgba(245, 245, 244, 1);
    }
    &.isnot-selected {
      background-color: rgba(245, 245, 244, 0.6);
    }
    @include hover {
      background-color: $mango;
    }
    @include easeIn(350ms, all);
    &--active {
      background-color: $mango;
    }
  }
  &__dropdown-title {
    margin-right: 5px;
  }
  &__dropdown-title,
  &__dropdown-choice {
    font-size: 15px;
    @include maxq(mobile-xs) {
      font-size: 12px;
    }
  }

  &__dropdown-chevron {
    padding-left: 19px;
    @include maxq(mobile-xs) {
      padding-left: 8px;
    }
  }
  &__chevron {
    @include easeIn(150ms, all);
    &--active {
      transform: rotate(180deg);
    }
  }

  .custom-dropdown {
    position: unset;
    padding: 0;
    .drop-content {
      width: 100%;
      &-in {
        padding: 0;
        min-width: unset;
        max-height: 200px;
        overflow-y: auto;
        transition: max-height 0.3s ease-out;
        scrollbar-width: thin;
        scrollbar-color: #d4d4d4 #f5f5f4;
        &::-webkit-scrollbar {
          width: 8px;
        }
        &::-webkit-scrollbar-track {
          background-color: #f5f5f4;
        }
        &::-webkit-scrollbar-thumb {
          background-color: #d4d4d4;
          background-color: #afafaf;
        }
        &::-webkit-scrollbar-button {
          display: none;
        }
      }
    }
  }

  &__item {
    padding: 20px 16px;
    font-size: 15px;
    line-height: 20px;
    color: $darkBlue;

    @include hover {
      background-color: $cultured;
    }

    &--active {
      background-color: $cultured;
    }
  }
}
</style>
